import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { AlertCircle, FlaskConical, Loader2, Plus } from 'lucide-react';
import { EvaluatorContent } from '../EvaluatorPage';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrganization } from '@/hooks/useOrganization';
import { useRetrySimulationBatch, useSimulationBatch, useSimulationBatches } from '@/hooks/useEvaluator';
import promptVersionsService from '@/lib/api/promptVersions';
import { ALL_POPULATIONS } from './constants';
import type { PageTab } from './types';
import { buildPersonaReviewItems, chooseDefaultRun, getPopulationLabel } from './utils';
import { ResultsToolbar } from './components/ResultsToolbar';
import { RunSimulationSheet } from './components/RunSimulationSheet';
import { ResultsView } from './views/ResultsView';
import { HistoryView } from './views/HistoryView';

const SimulationsPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRunId = searchParams.get('batch');
  const [activeTab, setActiveTab] = useState<PageTab>('history');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(requestedRunId);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [personasGenerateOpen, setPersonasGenerateOpen] = useState(false);
  const [runSheetOpen, setRunSheetOpen] = useState(searchParams.get('action') === 'run');

  const { data, isLoading, isError } = useSimulationBatches(orgId);
  const retrySimulationBatch = useRetrySimulationBatch();
  const runs = data?.batches ?? [];
  const requestedAction = searchParams.get('action');
  const requestedVersionParam = searchParams.get('version');
  const requestedVersion = requestedVersionParam && /^\d+$/.test(requestedVersionParam)
    ? Number(requestedVersionParam)
    : null;

  const { data: versionsData } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await promptVersionsService.listVersions(orgId);
      if (response.success && response.data) return response.data;
      throw new Error(response.error || 'Failed to load versions');
    },
    enabled: !!orgId,
  });

  const versions = versionsData?.versions ?? [];

  useEffect(() => {
    if (requestedRunId) {
      setSelectedRunId(requestedRunId);
      setActiveTab('results');
      return;
    }
    if (!selectedRunId && runs.length > 0) {
      setSelectedRunId(chooseDefaultRun(runs)?.id ?? null);
    }
  }, [requestedRunId, runs, selectedRunId]);

  useEffect(() => {
    setRunSheetOpen(requestedAction === 'run');
  }, [requestedAction]);

  const selectedRunSummary = useMemo(
    () => runs.find((run) => run.id === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  const {
    data: selectedRun,
    isLoading: runLoading,
    isError: runError,
  } = useSimulationBatch(selectedRunId, orgId);

  const conversations = selectedRun?.conversations ?? [];
  const hydratedSelectedRun = selectedRun ?? selectedRunSummary;
  const [selectedPopulation, setSelectedPopulation] = useState<string>(ALL_POPULATIONS);
  const populationOptions = useMemo(() => {
    const fromConversations = Array.from(
      new Set(buildPersonaReviewItems(conversations).map((persona) => persona.population)),
    );
    if (fromConversations.length > 0) {
      return fromConversations.sort((a, b) => a.localeCompare(b));
    }
    return Array.from(
      new Set((hydratedSelectedRun?.dataset_names ?? []).map((name) => getPopulationLabel(name))),
    ).sort((a, b) => a.localeCompare(b));
  }, [conversations, hydratedSelectedRun?.dataset_names]);

  useEffect(() => {
    if (selectedPopulation === ALL_POPULATIONS) return;
    if (!populationOptions.includes(selectedPopulation)) {
      setSelectedPopulation(ALL_POPULATIONS);
    }
  }, [populationOptions, selectedPopulation]);

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    setSelectedPersonaId(null);
    setSelectedConversationId(null);
    setSelectedPopulation(ALL_POPULATIONS);
    setActiveTab('results');
    const next = new URLSearchParams(searchParams);
    next.set('batch', runId);
    setSearchParams(next, { replace: true });
  };

  const handleRetryRun = (runId: string) => {
    if (!orgId) return;
    retrySimulationBatch.mutate({
      batchId: runId,
      organizationId: orgId,
    });
  };

  const openRunSheet = () => {
    const next = new URLSearchParams(searchParams);
    next.set('action', 'run');
    setSearchParams(next, { replace: false });
    setRunSheetOpen(true);
  };

  const handleRunSheetOpenChange = (open: boolean) => {
    setRunSheetOpen(open);
    if (!open && searchParams.get('action') === 'run') {
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('version');
      setSearchParams(next, { replace: true });
    }
  };

  const handleSimulationCreated = (batchId: string) => {
    setRunSheetOpen(false);
    setSelectedRunId(batchId);
    setSelectedPersonaId(null);
    setSelectedConversationId(null);
    setSelectedPopulation(ALL_POPULATIONS);
    setActiveTab('results');
    const next = new URLSearchParams(searchParams);
    next.set('batch', batchId);
    next.delete('action');
    next.delete('version');
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Simulations | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Simulations"
          titleTooltip="Review simulated personas, generated conversations, and performance."
        />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="min-w-0 flex-1 overflow-y-auto px-4 pb-8 pt-5 sm:px-6 md:px-8">
            {isLoading ? (
              <DataSurface>
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading simulations...
                </div>
              </DataSurface>
            ) : isError ? (
              <DataSurface>
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <p className="text-sm font-medium text-foreground">Could not load simulations.</p>
                  <p className="text-sm text-muted-foreground">Try again shortly.</p>
                </div>
              </DataSurface>
            ) : runs.length === 0 ? (
              <DataSurface>
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <FlaskConical className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No simulations yet</p>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                      Generate personas, then run a simulation to create conversations and scores.
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button size="sm" onClick={openRunSheet}>
                      <FlaskConical className="h-4 w-4" />
                      Run Simulation
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <Link to="/agents">Open Agent Versions</Link>
                    </Button>
                  </div>
                </div>
              </DataSurface>
            ) : (
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as PageTab)} className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <TabsList className="h-9 w-fit rounded-lg border border-border/80 bg-muted p-1">
                    <TabsTrigger value="history" className="h-7 rounded-md px-3 text-[13px]">
                      History
                    </TabsTrigger>
                    <TabsTrigger value="personas" className="h-7 rounded-md px-3 text-[13px]">
                      Personas
                    </TabsTrigger>
                    <TabsTrigger value="results" className="h-7 rounded-md px-3 text-[13px]">
                      Results
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeTab === 'results' ? (
                      <ResultsToolbar
                        runs={runs}
                        selectedRun={hydratedSelectedRun}
                        populations={populationOptions}
                        selectedPopulation={selectedPopulation}
                        onSelectRun={handleSelectRun}
                        onPopulationChange={(population) => {
                          setSelectedPopulation(population);
                          setSelectedPersonaId(null);
                          setSelectedConversationId(null);
                        }}
                        onOpenHistory={() => setActiveTab('history')}
                      />
                    ) : activeTab === 'personas' ? (
                      <Button size="sm" variant="outline" onClick={() => setPersonasGenerateOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Create Population
                      </Button>
                    ) : null}
                    <Button size="sm" onClick={openRunSheet}>
                      <FlaskConical className="h-4 w-4" />
                      Run Simulation
                    </Button>
                  </div>
                </div>

                <TabsContent value="results" className="mt-0">
                  <ResultsView
                    selectedRun={hydratedSelectedRun}
                    conversations={conversations}
                    selectedPersonaId={selectedPersonaId}
                    selectedConversationId={selectedConversationId}
                    selectedPopulation={selectedPopulation}
                    isLoading={runLoading}
                    isError={runError}
                    onSelectPersona={setSelectedPersonaId}
                    onSelectConversation={setSelectedConversationId}
                  />
                </TabsContent>

                <TabsContent value="personas" className="mt-0">
                  <EvaluatorContent
                    hideHeaderAction
                    generateOpen={personasGenerateOpen}
                    onGenerateOpenChange={setPersonasGenerateOpen}
                  />
                </TabsContent>

                <TabsContent value="history" className="mt-0">
                  <HistoryView
                    runs={runs}
                    selectedRunId={selectedRunId}
                    onSelectRun={handleSelectRun}
                    onRetryRun={handleRetryRun}
                    retryingRunId={retrySimulationBatch.isPending ? retrySimulationBatch.variables?.batchId ?? null : null}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
          <AnimatePresence mode="wait">
            {runSheetOpen && (
              <RunSimulationSheet
                open={runSheetOpen}
                onOpenChange={handleRunSheetOpenChange}
                organizationId={orgId}
                promptName={versionsData?.prompt_name}
                versions={versions}
                requestedVersion={requestedVersion}
                onSimulationCreated={handleSimulationCreated}
                onCreatePopulation={() => {
                  setActiveTab('personas');
                  handleRunSheetOpenChange(false);
                  setPersonasGenerateOpen(true);
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default SimulationsPage;
